import { inject, Injectable } from '@angular/core';

import { CustomerCreationResponse } from '../../model/create-customer.model';
import { DealerConsent } from '../../model/customer-information.model';
import { VehicleSearchQuery } from '../../model/customer-search-query.model';
import { BaseVehicle, EditVehicleRequest } from '../../model/customer-vehicle.model';
import { Customer } from '../../model/customer.model';
import { ExtendedCustomer } from '../../model/extended-customer.model';
import { FoundCustomer } from '../../model/found-customer.model';
import { RetailCrmNewCustomerRequestBodyMapper } from './mapper/request-body/customer-model.mapper';
import { CustomerVehicleMapper } from './mapper/request-body/customer-vehicle.mapper';
import { RetailCrmDealerConsentRequestBodyMapper } from './mapper/request-body/dealer-consent-model.mapper';
import { RetailCrmUpdateCustomerRequestBodyMapper } from './mapper/request-body/update-customer-model.mapper';
import { RetailCrmCreateAccountViewModelMapper } from './mapper/view-model/create-account-model.mapper';
import { RetailCrmCustomerViewModelMapper } from './mapper/view-model/customer-model.mapper';
import { RetailCrmExtendedCustomerViewModelMapper } from './mapper/view-model/extended-customer-model.mapper';
import { RetailCrmFoundCustomerViewModelMapper } from './mapper/view-model/found-customer-model.mapper';
import { RetailCrmAccountQueryOptions } from './model/requests/accounts.model';
import { RetailCrmAccountUpdateInput } from './model/requests/update-account.model';
import { RetailCrmRelatedDealersResponse } from './model/responses/legal-entities.model';
import { RetailCrmClient } from './retail-crm.client';

@Injectable({
  providedIn: 'root',
})
export class RetailCrmService {
  private readonly client = inject(RetailCrmClient);
  private readonly foundCustomerViewMapper = inject(RetailCrmFoundCustomerViewModelMapper);
  private readonly customerViewMapper = inject(RetailCrmCustomerViewModelMapper);
  private readonly extendedCustomerViewMapper = inject(RetailCrmExtendedCustomerViewModelMapper);
  private readonly createAccountMapper = inject(RetailCrmCreateAccountViewModelMapper);
  private readonly createAccountBodyMapper = inject(RetailCrmNewCustomerRequestBodyMapper);
  private readonly updateAccountBodyMapper = inject(RetailCrmUpdateCustomerRequestBodyMapper);
  private readonly customerVehicleMapper = inject(CustomerVehicleMapper);
  private readonly dealerConsentBodyMapper = inject(RetailCrmDealerConsentRequestBodyMapper);

  async searchAccounts(searchParams: RetailCrmAccountQueryOptions): Promise<FoundCustomer[]> {
    const retailCrmAccountsResponse = await this.client.searchAccounts(searchParams);
    return this.foundCustomerViewMapper.mapToFoundCustomers(retailCrmAccountsResponse);
  }

  async getAccountByAccountNumber(accountNumber: string): Promise<Customer> {
    const retailCrmAccountResponse = await this.client.getAccountByAccountNumber(accountNumber);
    return this.customerViewMapper.mapToCustomer(retailCrmAccountResponse);
  }

  async getAccountForComparisonByAccountNumber(accountNumber: string): Promise<ExtendedCustomer> {
    const retailCrmAccountResponse = await this.client.getAccountByAccountNumber(accountNumber);
    return this.extendedCustomerViewMapper.mapToExtendedCustomer(retailCrmAccountResponse);
  }

  async createAccount(newCustomer: Customer): Promise<CustomerCreationResponse> {
    const newAccountInput = this.createAccountBodyMapper.mapNewAccount(newCustomer);
    const createAccountResponse = await this.client.createAccount(newAccountInput);
    return this.createAccountMapper.mapCreateAccountResponse(createAccountResponse);
  }

  async searchCustomersViaVehicle(query: VehicleSearchQuery): Promise<FoundCustomer[]> {
    const vehiclesResponse = await this.client.searchVehicles(query);
    return this.foundCustomerViewMapper.mapVehiclesToFoundCustomers(vehiclesResponse);
  }

  async getVehicle(vin: string): Promise<BaseVehicle | null> {
    const vehicleResponse = await this.client.getVehicle(vin);
    if (!vehicleResponse) {
      return null;
    }
    return this.customerVehicleMapper.mapToCustomerVehicle(vehicleResponse);
  }

  async updateVehicleC2v(changeRequest: EditVehicleRequest): Promise<void> {
    await this.client.updateVehicleC2v(this.customerVehicleMapper.mapToUpdateC2vRequest(changeRequest));
  }

  /** @returns accountNumber from user in Retail CRM */
  async importFromGcdm(gcid: string): Promise<string | undefined> {
    return await this.client.importFromGcdm(gcid);
  }

  /** Updates customer information without dealer consents */
  async updateAccount(customer: Customer): Promise<string | undefined> {
    const { dealerConsent: dealerConsents, ...customerWithoutConsents } = customer;
    const accountUpdateInput: RetailCrmAccountUpdateInput =
      this.updateAccountBodyMapper.mapUpdateCustomer(customerWithoutConsents);
    return (await this.client.updateAccount(customerWithoutConsents.accountNumber, accountUpdateInput)).id;
  }

  /** Saves dealer consents without touching master data (CUSTAPP-597).
   * Still the general account-update endpoint - Retail CRM has no dedicated one yet - but the
   * payload is already the subset that endpoint will take, so adopting it is a URL change here.
   * Dealer consents are Retail CRM's for every customer, synced or local: the NSC Database does
   * not store them. Sending only the consent is what keeps that true without also writing master
   * data into Retail CRM for a customer the NSC Database owns (CUSTAPP-613).   *
   * Previous consents stored in Retail CRM are invalidated, so only call this when the consents
   * actually changed.
   */
  async updateDealerConsents(accountNumber: string, dealerConsent: DealerConsent): Promise<string | undefined> {
    const accountUpdateInput = this.dealerConsentBodyMapper.mapDealerConsent(dealerConsent);
    return (await this.client.updateAccount(accountNumber, accountUpdateInput)).id;
  }

  /**
   * Links a Retail CRM customer profile to an NSC Database (GCDM) customer profile.
   *
   * @param accountNumber identifies the customer profile in Retail CRM
   * @param gcid identifies the customer profile in the NSC Database
   */
  async syncWithGcdm(accountNumber: string, gcid: string): Promise<void> {
    await this.client.syncWithGcdm(accountNumber, gcid);
  }

  async getRelatedDealers(): Promise<RetailCrmRelatedDealersResponse> {
    return await this.client.getRelatedDealers();
  }
}
